// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// palette.ts — every colour that carries meaning, in one place, so tests/contrast.test.ts can pin
// it (principle #22). Nothing in the renderer may hard-code a colour: Scrapwall drew its walls at
// 1.14:1 against the board and they were invisible while every geometry check passed, because a
// literal buried in a draw call is a colour nobody can measure.
//
// The two runner colours are a blue/amber pair, which is the one hue opposition that survives all
// three common kinds of colour blindness — and neither is ever the ONLY cue: each runner also
// carries its own glyph and its goal edge is drawn in its own colour.

export const PALETTE = {
  /** Page behind everything. */
  bg: '#12150f',
  /** The panels the menus and HUD sit on. */
  panel: '#1d2318',
  panelDeep: '#161b12',
  /** The common itself — the surface every piece is drawn on. */
  board: '#232b1c',
  /** The lighter of the two turf tones; cells alternate so a step is legible. */
  turf: '#2b3423',
  /** The lattice between cells, which is where a hedge can go. */
  lattice: '#3c4732',

  /** Runner one, and the edge it is racing to. */
  runnerA: '#6cb6ff',
  /** Runner two. */
  runnerB: '#f2a950',
  /** Runner three (only in a 3-4 player race). */
  runnerC: '#e58ad6',
  /** Runner four. */
  runnerD: '#7de0c4',

  /** A planted hedge. */
  hedge: '#8ad06a',
  /** A hedge being aimed but not yet committed. */
  ghost: '#b9e39f',
  /** The outline that says "you may tap this". */
  target: '#ffffff',

  ink: '#101408',
  text: '#f2f5ec',
  textMuted: '#b9c4ab',
  good: '#8ad06a',
  bad: '#ff8a7a',
} as const;

export type PaletteKey = keyof typeof PALETTE;

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance per WCAG 2.1. Throws on anything it cannot parse, so a typo in a colour
 *  constant fails the test run instead of silently scoring 1:1. */
export function luminance(hex: string): number {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a #rrggbb colour: ${hex}`);
  const n = parseInt(m[1], 16);
  return (
    0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
  );
}

export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Colour is never the only cue — every runner carries a distinct glyph too. */
export const RUNNER_COLOURS = [PALETTE.runnerA, PALETTE.runnerB, PALETTE.runnerC, PALETTE.runnerD] as const;
export const RUNNER_GLYPHS = ['●', '▲', '■', '◆'] as const;

export const colourOf = (i: number): string => RUNNER_COLOURS[i % RUNNER_COLOURS.length];
export const glyphOf = (i: number): string => RUNNER_GLYPHS[i % RUNNER_GLYPHS.length];
